import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { of, tap } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { IExposureDuration } from '../models/exposure-duration.model';
import { ExposureDurationApiService } from '../services/exposure-duration-api.service';
import {
  ClearCurrentExposureDuration,
  CreateExposureDuration,
  DeleteExposureDuration,
  LoadAllExposureDurations,
  LoadExposureDurationById,
  LoadExposureDurations,
  UpdateExposureDuration,
} from './exposure-duration.actions';

export interface ExposureDurationStateModel {
  exposureDurations: IExposureDuration[];
  exposureDurationsList: IdName[];
  exposureDurationsTotalPages: number;
  currentExposureDuration: IExposureDuration | undefined;
}

const EXPOSURE_DURATION_STATE_TOKEN = new StateToken<ExposureDurationStateModel>(
  'exposureDuration'
);

@State<ExposureDurationStateModel>({
  name: EXPOSURE_DURATION_STATE_TOKEN,
  defaults: {
    exposureDurations: [],
    exposureDurationsList: [],
    exposureDurationsTotalPages: 0,
    currentExposureDuration: undefined,
  },
})
@Injectable()
export class ExposureDurationState {
  constructor(private service: ExposureDurationApiService) {}

  @Action(LoadExposureDurations)
  loadExposureDurations(
    { getState, setState }: StateContext<ExposureDurationStateModel>,
    { filter }: LoadExposureDurations
  ) {
    return this.service.loadExposureDurations(filter).pipe(
      tap((response: ApiResponse<IExposureDuration[]>) => {
        const state = getState();
        setState({
          ...state,
          exposureDurations: response.data,
          exposureDurationsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllExposureDurations)
  loadAllExposureDurations({ setState }: StateContext<ExposureDurationStateModel>) {
    return this.service.loadAllExposureDurations().pipe(
      tap((response: IdName[]) => {
        setState(patch({ exposureDurationsList: response }));
      })
    );
  }

  @Action(LoadExposureDurationById, { cancelUncompleted: true })
  loadExposureDurationById(
    { getState, setState }: StateContext<ExposureDurationStateModel>,
    { id }: LoadExposureDurationById
  ) {
    const existing = getState().exposureDurations.find((item) => item.id === id);
    if (existing) {
      setState(patch({ currentExposureDuration: existing }));
      return of(existing);
    }

    return this.service.loadExposureDurationById(id).pipe(
      tap((exposureDuration) => {
        if (!exposureDuration) {
          return;
        }
        setState(
          patch({
            currentExposureDuration: exposureDuration,
          })
        );
      })
    );
  }

  @Action(CreateExposureDuration)
  @OnSuccess({ message: 'exposureDurationAdded' })
  createExposureDuration(
    { setState }: StateContext<ExposureDurationStateModel>,
    { exposureDuration }: CreateExposureDuration
  ) {
    return this.service.createExposureDuration(exposureDuration).pipe(
      tap((response: IExposureDuration) => {
        setState(
          patch({
            exposureDurations: insertItem<IExposureDuration>(response),
          })
        );
      })
    );
  }

  @Action(UpdateExposureDuration)
  @OnSuccess({ message: 'exposureDurationUpdated' })
  updateExposureDuration(
    { setState }: StateContext<ExposureDurationStateModel>,
    { exposureDuration }: UpdateExposureDuration
  ) {
    return this.service.updateExposureDuration(exposureDuration).pipe(
      tap(() => {
        setState(
          patch({
            exposureDurations: updateItem<IExposureDuration>(
              (toUpdate) => toUpdate?.id === exposureDuration.id,
              exposureDuration
            ),
          })
        );
      })
    );
  }

  @Action(DeleteExposureDuration, { cancelUncompleted: true })
  @OnSuccess({
    message: 'exposureDurationDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteExposureDuration(
    ctx: StateContext<ExposureDurationStateModel>,
    { id }: DeleteExposureDuration
  ) {
    return this.service.deleteExposureDuration(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            exposureDurations: removeItem<IExposureDuration>(
              (toDelete: IExposureDuration | undefined) => toDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentExposureDuration)
  clearCurrentExposureDuration({ setState }: StateContext<ExposureDurationStateModel>) {
    setState(
      patch({
        currentExposureDuration: undefined,
      })
    );
  }

  @Selector()
  static getExposureDurationsTotalPages(state: ExposureDurationStateModel) {
    return state.exposureDurationsTotalPages;
  }

  @Selector()
  static exposureDurations(state: ExposureDurationStateModel) {
    return state.exposureDurations;
  }

  @Selector()
  static getExposureDurationsList(state: ExposureDurationStateModel) {
    return state.exposureDurationsList;
  }

  @Selector()
  static getCurrentExposureDuration(state: ExposureDurationStateModel) {
    return state.currentExposureDuration;
  }
}
